<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_motion_blind_time_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_motion_blind_time_android,
	} from 'src/services/native_container_requests_android';

	import { app, os } from 'src/stores/session/native-bridge';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import {
		isBleSettingsSessionReady,
		persistBleDeviceSetting,
		readStoredBleDevice,
		resumeBleSettingsConnectionIfNeeded,
	} from 'src/helpers/ble_settings_session';
	import { valueOutOfRange } from 'src/helpers/utilities';

	import { Button, Text, Input } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindSettingsDrilldownBack, bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import DynamicAlert from './DynamicAlert.svelte';

	export let device;

	const min = 30;
	const max = 600;
	let motionBlindTime = readStoredBleDevice(device)?.motionBlindTime || min;
	let hadSession = false;

	$: sessionReady = isBleSettingsSessionReady($controlBleDevice.connectionState);
	$: if (sessionReady) hadSession = true;
	$: resumeBleSettingsConnectionIfNeeded(device.id, $controlBleDevice);
	$: controlsDisabled = !sessionReady || !$app;

	const goBackToSettingsList = bindSettingsDrilldownBack();

	const setMotionBlindTime = () => {
		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_motion_blind_time_ios(device.id, motionBlindTime)
				: control_bluetooth_motion_blind_time_android(device.id, motionBlindTime);

			persistBleDeviceSetting(device, { motionBlindTime });
			goBackToSettingsList?.();
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	$: error = valueOutOfRange(motionBlindTime, min, max)
		? $format('error_out_of_range', {
				default: `Value ${motionBlindTime} is out of range. Please provide a value within the limits. (${min} — ${max})`,
				values: { value: motionBlindTime, max, min, unit: 'sec.' },
			})
		: '';

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: setMotionBlindTime,
				loading: false,
				disabled: controlsDisabled || !!error,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

<DynamicAlert showExpiredMsg={hadSession} />

<Text styleType="body-small" class="mb-s">
	{$format('text_motion_ble_blind_time', {
		default: 'Blind time in seconds after last detected motion.',
	})}
</Text>

<Input
	aria-label={$format('input_motion_ble_blind_time', {
		default:
			'Enter the blind time in seconds after last detected motion. Value must be between {min} — {max}!',
		values: { min, max },
	})}
	icon="fas fa-eye-slash"
	class="mb-m"
	bind:value={motionBlindTime}
	type="number"
	step={1}
	{min}
	{max}
	disabled={controlsDisabled}
	info={$format('movement_limit_toast', {
		default: 'Values must be between {min} — {max}!',
		values: { min, max },
	})}
	{error}
	id="input-6r578gd"
/>

{#if !$showNewLayout}
	<Button disabled={controlsDisabled || !!error} size="m" width="full" on:click={setMotionBlindTime}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
